import { writeEmotesOthers, type SettingsStore } from "./settings.ts";

/**
 * Where the emoji over characters read the Show other players' emotes choice each frame: the game report's emoji entry.
 */
export interface EmotesOthersTarget {
  others: boolean;
}

/**
 * The page's Show other players' emotes choice (P2.19-T7, ADR-114 decision 5.8), as `IdleMarksSetting` holds Show idle
 * and AFK marks: made from what the store holds as the page loads, put on the target the emoji surface reads every
 * frame, and taking each change Settings makes, which applies on the next frame and is kept per device. Off, the page
 * draws no other character's emoji and still draws the player's own; actions are chat and are not touched. It changes
 * only what this viewer draws; nothing is sent.
 *
 * @example
 * ```ts
 * const emotesOthers = new EmotesOthersSetting(gameReport.emoji, readEmotesOthers());
 * emotesOthers.setShow(false);
 * ```
 */
export class EmotesOthersSetting {
  /**
   * @param target Where the emoji surface reads the choice.
   * @param show Whether other players' emoji are drawn as the page loads, as `readEmotesOthers` gives it.
   * @param store Where a change is kept; this browser's `localStorage` when omitted.
   */
  constructor(
    private readonly target: EmotesOthersTarget,
    show: boolean,
    private readonly store?: SettingsStore | null,
  ) {
    target.others = show;
  }

  /**
   * Whether other players' emoji are drawn now.
   *
   * @returns The choice.
   */
  get show(): boolean {
    return this.target.others;
  }

  /**
   * Takes the viewer's choice, which applies on the next frame, and keeps it.
   *
   * @param show Whether other players' emoji are drawn.
   */
  setShow(show: boolean): void {
    this.target.others = show;
    writeEmotesOthers(show, this.store);
  }
}
